/**
 * P12.4 — ProjectReviewOverrideDialog
 *
 * Required gate to override system-derived install plan values.
 *   • Typed CONFIRM phrase
 *   • Reason category (required)
 *   • Reason note (≥10 chars)
 *   • Optional override fields (days, crewSize, payoutAmount, arrivalNote)
 *
 * Caller wires the actual `appendPlanningOverride` + Job mutation in
 * onConfirm. This dialog is presentation-only.
 */

import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PLANNING_OVERRIDE_REASON_LABEL,
  type PlanningOverrideReasonCategory,
  type PlanningOverrideValues,
  type PlanningSystemEstimateSnapshot,
} from "@/data/planningReview/types";

export const PLANNING_REVIEW_CONFIRM_PHRASE = "CONFIRM";
export const PLANNING_REVIEW_REASON_MIN = 10;

interface Props {
  open: boolean;
  jobLabel: string;
  systemPlan: PlanningSystemEstimateSnapshot;
  onCancel: () => void;
  onConfirm: (input: {
    override: PlanningOverrideValues;
    reasonCategory: PlanningOverrideReasonCategory;
    reasonNote: string;
  }) => void;
}

export function ProjectReviewOverrideDialog({
  open,
  jobLabel,
  systemPlan,
  onCancel,
  onConfirm,
}: Props) {
  const [days, setDays] = useState<string>("");
  const [crewSize, setCrewSize] = useState<string>("");
  const [payoutAmount, setPayoutAmount] = useState<string>("");
  const [arrivalNote, setArrivalNote] = useState<string>("");
  const [reasonCategory, setReasonCategory] =
    useState<PlanningOverrideReasonCategory | "">("");
  const [reasonNote, setReasonNote] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const reset = () => {
    setDays("");
    setCrewSize("");
    setPayoutAmount("");
    setArrivalNote("");
    setReasonCategory("");
    setReasonNote("");
    setConfirm("");
    setErr(null);
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const handleConfirm = () => {
    const override: PlanningOverrideValues = {};
    if (days.trim()) {
      const d = Number(days);
      if (!Number.isFinite(d) || d <= 0) {
        setErr("Days must be a positive number.");
        return;
      }
      override.days = d;
    }
    if (crewSize.trim()) {
      const c = Number(crewSize);
      if (!Number.isFinite(c) || c <= 0) {
        setErr("Crew size must be a positive number.");
        return;
      }
      override.crewSize = c;
    }
    if (payoutAmount.trim()) {
      const p = Number(payoutAmount);
      if (!Number.isFinite(p) || p < 0) {
        setErr("Payout must be a non-negative number.");
        return;
      }
      override.payoutAmount = p;
    }
    if (arrivalNote.trim()) override.arrivalNote = arrivalNote.trim();

    if (Object.keys(override).length === 0) {
      setErr("Set at least one override value, or use Approve as-is.");
      return;
    }
    if (!reasonCategory) {
      setErr("Select a reason category.");
      return;
    }
    if (reasonNote.trim().length < PLANNING_REVIEW_REASON_MIN) {
      setErr(
        `Reason note must be at least ${PLANNING_REVIEW_REASON_MIN} characters.`,
      );
      return;
    }
    if (confirm.trim() !== PLANNING_REVIEW_CONFIRM_PHRASE) {
      setErr(`Type ${PLANNING_REVIEW_CONFIRM_PHRASE} exactly to proceed.`);
      return;
    }
    onConfirm({
      override,
      reasonCategory: reasonCategory as PlanningOverrideReasonCategory,
      reasonNote: reasonNote.trim(),
    });
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleCancel()}>
      <DialogContent
        data-testid="planning-review-override-dialog"
        className="max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-warning" />
            Override planning estimate
          </DialogTitle>
          <DialogDescription className="text-xs">
            {jobLabel}. System estimate is preserved. Your override is
            archived for future rule improvement.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="space-y-1">
            <Label htmlFor="ov-days">Days (system: {systemPlan.days})</Label>
            <Input
              id="ov-days"
              data-testid="ov-days"
              type="number"
              min={1}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder={String(systemPlan.days)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ov-crew">Crew (system: {systemPlan.crewSize})</Label>
            <Input
              id="ov-crew"
              data-testid="ov-crew"
              type="number"
              min={1}
              value={crewSize}
              onChange={(e) => setCrewSize(e.target.value)}
              placeholder={String(systemPlan.crewSize)}
            />
          </div>
          <div className="space-y-1 col-span-2">
            <Label htmlFor="ov-payout">
              Crew payout (system: ${systemPlan.payoutAmount.toLocaleString()})
            </Label>
            <Input
              id="ov-payout"
              data-testid="ov-payout"
              type="number"
              min={0}
              value={payoutAmount}
              onChange={(e) => setPayoutAmount(e.target.value)}
              placeholder={String(systemPlan.payoutAmount)}
            />
          </div>
          <div className="space-y-1 col-span-2">
            <Label htmlFor="ov-arrival">Arrival note (optional)</Label>
            <Input
              id="ov-arrival"
              data-testid="ov-arrival"
              value={arrivalNote}
              onChange={(e) => setArrivalNote(e.target.value)}
              placeholder="e.g. Push start to 9:00 AM for HOA access"
            />
          </div>
        </div>

        <div className="space-y-2 pt-1">
          <Label className="text-xs">Reason category</Label>
          <Select
            value={reasonCategory}
            onValueChange={(v) =>
              setReasonCategory(v as PlanningOverrideReasonCategory)
            }
          >
            <SelectTrigger data-testid="ov-reason-category">
              <SelectValue placeholder="Select a reason" />
            </SelectTrigger>
            <SelectContent>
              {(
                Object.keys(
                  PLANNING_OVERRIDE_REASON_LABEL,
                ) as PlanningOverrideReasonCategory[]
              ).map((k) => (
                <SelectItem key={k} value={k}>
                  {PLANNING_OVERRIDE_REASON_LABEL[k]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ov-note" className="text-xs">
            Reason note ({PLANNING_REVIEW_REASON_MIN}+ chars)
          </Label>
          <Textarea
            id="ov-note"
            data-testid="ov-reason-note"
            rows={3}
            value={reasonNote}
            onChange={(e) => setReasonNote(e.target.value)}
            placeholder="What changed vs. the system estimate?"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="ov-confirm" className="text-xs">
            Type {PLANNING_REVIEW_CONFIRM_PHRASE} to proceed
          </Label>
          <Input
            id="ov-confirm"
            data-testid="ov-confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        {err && (
          <p data-testid="ov-error" className="text-[11px] text-destructive">
            {err}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            size="sm"
            data-testid="ov-submit"
            onClick={handleConfirm}
          >
            Save override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ProjectReviewOverrideDialog;
